/**
 * Registry of the schedule-job follows that drive UI side effects (toasts, refetches).
 * A follow left behind by a page, a scenario switch or a logout must stop instead of
 * toasting and refetching on whatever the user is looking at by then.
 * No imports on purpose: SecurityActions, scenarioActions and the store depend on it.
 */
const follows = new Map(); // AbortController -> { scenarioId, key, untilLogout }

/**
 * `key` (optional) names the follow for cancelScheduleFollow, e.g. a progress card's job id.
 * `untilLogout`: a detached request with no UI (it must outlive the page and the scenario
 * switch that left it behind) is stopped by the end of the session only.
 */
export function registerScheduleFollow(scenarioId, key = null, { untilLogout = false } = {}) {
  const controller = new AbortController();
  follows.set(controller, {
    scenarioId: String(scenarioId),
    key: key == null ? null : String(key),
    untilLogout,
  });
  return controller;
}

export function unregisterScheduleFollow(controller) {
  follows.delete(controller);
}

/**
 * Aborts the registered follows, all of them or, with `exceptScenarioId`, those on
 * another scenario than that one. The `untilLogout` ones only when `logout` is true.
 */
export function cancelScheduleFollows({ exceptScenarioId, logout = false } = {}) {
  const keep = exceptScenarioId == null ? null : String(exceptScenarioId);
  [...follows].forEach(([controller, { scenarioId, untilLogout }]) => {
    if (untilLogout && !logout) return;
    if (keep !== null && scenarioId === keep) return;
    follows.delete(controller);
    controller.abort();
  });
}

/** Aborts the registered follows named `key`. */
export function cancelScheduleFollow(key) {
  if (key == null) return;
  [...follows].forEach(([controller, entry]) => {
    if (entry.key !== String(key)) return;
    follows.delete(controller);
    controller.abort();
  });
}

/**
 * Whatever route opens another scenario (a grid, the URL, a breadcrumb, browser Back) ends in
 * `state.scenario.item` taking its id: from then on the follows of the other scenarios stop.
 * An item emptied while the same scenario reloads is no switch.
 */
export function cancelFollowsOnScenarioSwitch(store) {
  const openId = () => {
    const id = store.getState()?.scenario?.item?.id;
    return id == null ? null : String(id);
  };
  let lastOpenId = openId();
  return store.subscribe(() => {
    const id = openId();
    if (id === null || id === lastOpenId) return;
    lastOpenId = id;
    cancelScheduleFollows({ exceptScenarioId: id });
  });
}
